
import { IUserRoles, defaultUserRoles } from '../Interfaces/IUserRoles';
import { IReportListResponse } from "../Interfaces/IReports";

export enum RoleList {
  SATAdmin = 'SAT.Admin',
  ReportUser = 'ReportUser.Admin',
  IFTSATAdmin = 'IFT_SAT.Admin', //Combined Admin role for IFT and SAT
  IntakeAdmin = 'Intake.Admin',//Intake Form Tool - Admin role
  SeveranceAdmin = 'Severance.Admin',//Severance Administration Tool - Admin role
  IntakeUser = 'Intake.User',//Intake Form Tool - User role without security page
};

export const RoleMapping: Record<RoleList, keyof IUserRoles> = {
  [RoleList.SATAdmin]: 'SATAdmin',
  [RoleList.ReportUser]: 'ReportUser',
  [RoleList.IFTSATAdmin]: 'IFTSATAdmin',
  [RoleList.IntakeAdmin]: 'IntakeAdmin',
  [RoleList.SeveranceAdmin]: 'SeveranceAdmin',
  [RoleList.IntakeUser]: 'IntakeUser',
};

export const FOLDER_ACCESS: Record<string, { roles: RoleList[]; order: number }> = {
  'Admin Upload': { roles: [RoleList.SATAdmin], order: 1 },
  'Packet Generation': { roles: [RoleList.SATAdmin], order: 2 },
  'SAT Reports': { roles: [RoleList.SATAdmin, RoleList.ReportUser], order: 3 },
  'IFT': { roles: [RoleList.SATAdmin, RoleList.IFTSATAdmin, RoleList.IntakeAdmin, RoleList.IntakeUser], order: 4 },
}

export const PAGE_ACCESS: Record<string, RoleList[]> = {
  '/Security': [RoleList.SATAdmin, RoleList.IFTSATAdmin, RoleList.IntakeAdmin, RoleList.SeveranceAdmin],

  '/JobEliminationTrackingSummary': [RoleList.SATAdmin, RoleList.ReportUser],
  '/CompareGroupDetailReport': [RoleList.SATAdmin, RoleList.ReportUser],
  '/HRAssistReport': [RoleList.SATAdmin, RoleList.ReportUser],
  '/CombinedImpactedEmployeeDetailReport': [RoleList.SATAdmin, RoleList.ReportUser],
  '/IntakeFormDetailReport': [RoleList.SATAdmin, RoleList.ReportUser],
  '/IntakeFormSummaryReport': [RoleList.SATAdmin, RoleList.ReportUser],
  '/IntakeFormToolSecurityReport': [RoleList.SATAdmin, RoleList.ReportUser],
  '/PendingDataChangesReport': [RoleList.SATAdmin, RoleList.ReportUser],
  '/CompareGroupSummaryReport': [RoleList.SATAdmin, RoleList.ReportUser],
  '/NonUSImpactedEmployeeDetailReport': [RoleList.SATAdmin, RoleList.ReportUser],
  '/WARNEstimate': [RoleList.SATAdmin, RoleList.ReportUser],
  '/CountryHRDIntakeSummaryReport': [RoleList.SATAdmin, RoleList.ReportUser],
  '/ImpactedEmployeeDetailReport': [RoleList.SATAdmin, RoleList.ReportUser],

  '/PacketGeneration': [RoleList.SATAdmin],
  '/DocumentMaintenance': [RoleList.SATAdmin],
  '/DocumentRuleMaintenance': [RoleList.SATAdmin],
  '/PacketEmail': [RoleList.SATAdmin],
  '/GenerateEmail': [RoleList.SATAdmin],
  '/PacketProcessor': [RoleList.SATAdmin],
  '/TemplateFolder': [RoleList.SATAdmin],
  '/SharePointPacketProcess': [RoleList.SATAdmin],

  '/EmailMessageSettings': [RoleList.SATAdmin],
  '/CountryArea': [RoleList.SATAdmin],
  '/NonEmployeePerson': [RoleList.SATAdmin],
  '/FileImport': [RoleList.SATAdmin],
  '/ExcelTemplate': [RoleList.SATAdmin],
  '/ImpactIntakeEstimates': [RoleList.SATAdmin],
  '/PayrollBatchAssign': [RoleList.SATAdmin],

  '/IntakeForm': [RoleList.SATAdmin, RoleList.IFTSATAdmin, RoleList.IntakeAdmin, RoleList.IntakeUser],
  '/IntakeForm/:id': [RoleList.SATAdmin, RoleList.IFTSATAdmin, RoleList.IntakeAdmin, RoleList.IntakeUser],
  '/ManageIntakeForm': [RoleList.SATAdmin, RoleList.IFTSATAdmin, RoleList.IntakeAdmin, RoleList.IntakeUser],
  '/ManageIntakeForm/:intakeFormId?': [RoleList.SATAdmin, RoleList.IFTSATAdmin, RoleList.IntakeAdmin, RoleList.IntakeUser],
}
//Report name list
export enum ReportRoleList {
  JobEliminationTrackingSummary = 'JobEliminationTrackingSummary',
  WARNEstimate = 'WARNEstimate',
  IntakeFormSummaryReport = 'IntakeFormSummaryReport',
  IntakeFormDetailReport = 'IntakeFormDetailReport',
  NonUSImpactedEmployeeDetailReport = 'NonUSImpactedEmployeeDetailReport',
  IntakeFormToolSecurityReport = 'IntakeFormToolSecurityReport',
  CombinedImpactedEmployeeDetailReport = 'CombinedImpactedEmployeeDetailReport',
  CompareGroupDetailReport = 'CompareGroupDetailReport',
  HRAssistReport = 'HRAssistReport',
  PendingDataChangesReport = 'PendingDataChangesReport',
  CompareGroupSummaryReport = 'CompareGroupSummaryReport',
  CountryHRDIntakeSummaryReport = 'CountryHRDIntakeSummaryReport',
  ImpactedEmployeeDetailReport = 'ImpactedEmployeeDetailReport',
}
// Map report names to the ReportRoleList enum
export const reportNameToRoleMap: Record<string, keyof typeof ReportRoleList> = {
  'Job Elimination Tracking Summary': ReportRoleList.JobEliminationTrackingSummary,
  'WARN Estimate': ReportRoleList.WARNEstimate,
  'Intake Form Summary Report': ReportRoleList.IntakeFormSummaryReport,
  'Intake Form Detail Report': ReportRoleList.IntakeFormDetailReport,
  'Non-US Impacted Employee Detail Report': ReportRoleList.NonUSImpactedEmployeeDetailReport,
  'Intake Form Tool Security Report': ReportRoleList.IntakeFormToolSecurityReport,
  'Combined Impacted Employee Detail Report': ReportRoleList.CombinedImpactedEmployeeDetailReport,
  'Compare Group Detail Report': ReportRoleList.CompareGroupDetailReport,
  'HRAssist Report': ReportRoleList.HRAssistReport,
  'Pending Data Changes Report': ReportRoleList.PendingDataChangesReport,
  'Compare Group Summary Report': ReportRoleList.CompareGroupSummaryReport,
  'Country HRD Intake Summary Report': ReportRoleList.CountryHRDIntakeSummaryReport,
  'Impacted Employee Detail Report': ReportRoleList.ImpactedEmployeeDetailReport,
};

// create roles object with readable names and boolean values
export const getUserRoles = (roles?: string[] | null, reportList?: IReportListResponse[] | null): IUserRoles => {
  if (!roles || roles === null || roles === undefined) {
    return null;
  }
  const result: IUserRoles = {
    ...defaultUserRoles,
    SATAdmin: Array.isArray(roles) && roles.includes(RoleList.SATAdmin),
    ReportUser: Array.isArray(roles) && roles.includes(RoleList.ReportUser),
    IFTSATAdmin: Array.isArray(roles) && roles.includes(RoleList.IFTSATAdmin),
    IntakeAdmin: Array.isArray(roles) && roles.includes(RoleList.IntakeAdmin),
    SeveranceAdmin: Array.isArray(roles) && roles.includes(RoleList.SeveranceAdmin),
    IntakeUser: Array.isArray(roles) && roles.includes(RoleList.IntakeUser),
  }
  // Add individual report access based on reportList
  if (reportList && Array.isArray(reportList)) {
    reportList.forEach(report => {
      const reportRole = reportNameToRoleMap[report.reportName];
      if (reportRole && reportRole in result) {
        result[reportRole] = true;
      }
    });
  }
  return result;
}
//User Access role check
//SatAdmin user role check for application admin pages
export function hasSATAdmin(roles: IUserRoles): boolean {
  return roles && roles?.SATAdmin;
}
//Report user role check for all the reports
export function hasReportRole(roles: IUserRoles | null, reportName: keyof IUserRoles): boolean {
  return roles && (roles?.ReportUser && roles[reportName] === true);
}
//Check if user has any report access (for navigation)
export function hasAnyReportRole(roles: IUserRoles | null): boolean {
  return roles && roles?.ReportUser;
}
//Ift Sat Admin role check for security page
export function hasIftSatAdminRole(roles: IUserRoles | null): boolean {
  return roles && (roles?.IFTSATAdmin || roles?.IntakeAdmin || roles?.SeveranceAdmin);
}
//when user has no roles
export function hasNoRole(roles: IUserRoles | null): boolean {
  return !roles || (!roles?.SATAdmin && !roles?.ReportUser && !roles?.IFTSATAdmin && !roles?.IntakeAdmin && !roles?.SeveranceAdmin && !roles?.IntakeUser);
}

// When use Intake Form Admin has full access to application.
export function hasIntakeFormToolAdminRole(roles: IUserRoles | null): boolean {
  return roles && (roles?.IFTSATAdmin || roles?.IntakeAdmin);
}

// When use Intake Form Admin has full access to application.
export function hasSeveranceToolAdminRole(roles: IUserRoles | null): boolean {
  return roles && (roles?.IFTSATAdmin || roles?.SeveranceAdmin);
}
// Intake form tool User role - user role without security page access
export function hasIntakeFormToolUserRole(roles: IUserRoles | null): boolean {
  return roles && roles?.IntakeUser;
}